import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import {
  COMPOSITION_GET_DATA_V3,
  PositionsEntityKind,
} from '@revelio/data-access';

import { isEmptyCompositionBucket } from '../../CustomDash/components/DataProvider/composition/composition-v3/is-empty-composition-bucket';
import { buildCompositionV3Variables } from '../../CustomDash/filters/utils/composition-v3/build-composition-v3-variables';
import { positionsEntityKindToEntKind } from '../../CustomDash/filters/utils/role-skill-ent-kind';
import type { AdjacentRole } from './useAdjacentRoles';
import type { ResolvedSkill } from './useMapUserSkills';
import type { RoleGranularity } from './useResolveProfileIds';

/** A skill common in an adjacent role with whether the user already has it */
export interface RoleSkillComparison {
  name: string;
  /** Share of people in this role who have this skill (0-100 from API) */
  share: number;
  /** Whether the user already has this skill */
  userHas: boolean;
}

/** Skill comparison data for a single adjacent role */
export interface AdjacentRoleSkillData {
  roleId: number;
  /** Top skills in this role, sorted by share descending */
  topSkills: RoleSkillComparison[];
  /** Skills common in this role that the user doesn't have */
  skillGaps: RoleSkillComparison[];
  /** Skills the user has that are also common in this role */
  sharedSkills: RoleSkillComparison[];
}

interface UseAdjacentRoleSkillsResult {
  skillsByRole: Map<number, AdjacentRoleSkillData>;
  isLoading: boolean;
  progress: number;
}

const MAX_ROLES_TO_ENRICH = 7;
const TOP_SKILLS_LIMIT = 15;
const MIN_SKILL_SHARE = 1; // Minimum share % to include a skill

const ROLE_GRANULARITY_TO_ENTITY: Record<RoleGranularity, PositionsEntityKind> =
  {
    role_k15000: PositionsEntityKind.RoleK15000,
    role_k1500: PositionsEntityKind.RoleK1500,
    role_k150: PositionsEntityKind.RoleK150,
    role_k7: PositionsEntityKind.RoleK7,
  };

export const useAdjacentRoleSkills = (
  adjacentRoles: AdjacentRole[],
  userSkills: ResolvedSkill[],
  roleGranularity: RoleGranularity = 'role_k150'
): UseAdjacentRoleSkillsResult => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<Map<number, AdjacentRoleSkillData>>(
    new Map()
  );
  const prevRolesRef = useRef<string>('');

  const rolesToEnrich = adjacentRoles.slice(0, MAX_ROLES_TO_ENRICH);

  // Build a set of user skill names for fast lookup
  const userSkillNameSet = useMemo(
    () => new Set(userSkills.map((s) => s.name.toLowerCase().trim())),
    [userSkills]
  );

  // Reset when adjacent roles change
  const rolesKey = rolesToEnrich.map((r) => r.id).join(',');
  useEffect(() => {
    if (rolesKey !== prevRolesRef.current) {
      prevRolesRef.current = rolesKey;
      setCurrentIndex(0);
      setResults(new Map());
    }
  }, [rolesKey]);

  const currentRole =
    currentIndex < rolesToEnrich.length
      ? rolesToEnrich[currentIndex]
      : undefined;

  const entityType =
    ROLE_GRANULARITY_TO_ENTITY[roleGranularity] ?? PositionsEntityKind.RoleK150;

  const compositionVariables = useMemo(() => {
    if (!currentRole || currentIndex >= rolesToEnrich.length) {
      return buildCompositionV3Variables({ entities: [] });
    }

    return buildCompositionV3Variables({
      entities: [
        {
          EntID: currentRole.id,
          EntType: positionsEntityKindToEntKind(entityType),
        },
      ],
    });
  }, [currentRole, currentIndex, rolesToEnrich.length, entityType]);

  const [{ data, fetching, error }] = useQuery({
    query: COMPOSITION_GET_DATA_V3,
    variables: compositionVariables,
    pause: !currentRole || currentIndex >= rolesToEnrich.length,
  });

  // When data arrives, process skill comparison and advance
  useEffect(() => {
    if (!currentRole || fetching || currentIndex >= rolesToEnrich.length) {
      return;
    }

    if (data || error) {
      const skillData = processSkillData(
        currentRole.id,
        data?.composition_v3,
        userSkillNameSet
      );

      setResults((prev) => {
        const next = new Map(prev);
        next.set(currentRole.id, skillData);
        return next;
      });
      setCurrentIndex((prev) => prev + 1);
    }
  }, [
    data,
    error,
    fetching,
    currentRole,
    currentIndex,
    rolesToEnrich.length,
    userSkillNameSet,
  ]);

  const isLoading =
    rolesToEnrich.length > 0 && currentIndex < rolesToEnrich.length;

  const progress =
    rolesToEnrich.length > 0 ? currentIndex / rolesToEnrich.length : 1;

  return {
    skillsByRole: results,
    isLoading,
    progress,
  };
};

type CompositionV3SkillData = {
  entities?: ReadonlyArray<{
    metrics?: {
      skills?: ReadonlyArray<{
        id: number;
        short_name: string;
        long_name: string;
        headcount: ReadonlyArray<{ share: number }>;
      }>;
    };
  }>;
};

/** Extract and compare skills from the composition_v3 response */
function processSkillData(
  roleId: number,
  data: CompositionV3SkillData | null | undefined,
  userSkillNames: Set<string>
): AdjacentRoleSkillData {
  const empty: AdjacentRoleSkillData = {
    roleId,
    topSkills: [],
    skillGaps: [],
    sharedSkills: [],
  };

  const skills = data?.entities?.[0]?.metrics?.skills;
  if (!skills) return empty;

  const allSkills: RoleSkillComparison[] = skills
    .filter((skill) => !isEmptyCompositionBucket(skill.id))
    .map((skill) => {
      const name = skill.long_name || skill.short_name || '';
      const hc = skill.headcount;
      const latestShare = hc.length > 0 ? (hc[hc.length - 1]?.share ?? 0) : 0;
      const userHas = userSkillNames.has(name.toLowerCase().trim());

      return { name, share: latestShare, userHas };
    })
    .filter((s) => s.name && s.share >= MIN_SKILL_SHARE)
    .sort((a, b) => b.share - a.share);

  const topSkills = allSkills.slice(0, TOP_SKILLS_LIMIT);
  const skillGaps = topSkills.filter((s) => !s.userHas);
  const sharedSkills = topSkills.filter((s) => s.userHas);

  return {
    roleId,
    topSkills,
    skillGaps,
    sharedSkills,
  };
}
